import { useState, useEffect, useRef } from "react";
import * as Y from "yjs";

interface UseYTextResult {
  content: string;
  handleChange: (newContent: string) => void;
}

/**
 * Manages a controlled textarea that is backed by a Y.Text field.
 *
 * Observes the named text field on `yDoc` and keeps `content` in sync.
 * `handleChange` writes through to the Y.Text using a suppress ref so
 * the observer does not echo the local edit back to React state.
 *
 * Returns `{ content, handleChange }`.
 */
export function useYText(yDoc: Y.Doc | null, key: string): UseYTextResult {
  const [content, setContent] = useState("");
  const suppressRef = useRef(false);

  useEffect(() => {
    if (!yDoc) return;

    const yText = yDoc.getText(key);
    setContent(yText.toString());

    const observer = () => {
      if (!suppressRef.current) {
        setContent(yText.toString());
      }
    };
    yText.observe(observer);
    return () => yText.unobserve(observer);
  }, [yDoc, key]);

  const handleChange = (newContent: string) => {
    if (!yDoc) return;
    const yText = yDoc.getText(key);
    const oldContent = yText.toString();

    suppressRef.current = true;
    yDoc.transact(() => {
      yText.delete(0, oldContent.length);
      yText.insert(0, newContent);
    });
    suppressRef.current = false;
    setContent(newContent);
  };

  return { content, handleChange };
}
